import { getAuthToken } from '../utils/authUtils';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// Cache for feature flags to avoid repeated server calls
const featureFlagCache = new Map();

/**
 * Secure Feature Flag Service
 * Uses backend API endpoints instead of direct API access
 */
export const featureFlagService = {
  /**
   * Get enabled features for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to enabled features object
   */
  getEnabledFeatures: async (facilityId) => {
    try {
      console.log('Fetching enabled features for facility via API:', facilityId);
      const token = await getAuthToken();
      
      // Check cache first
      const cacheKey = `${facilityId}`;
      if (featureFlagCache.has(cacheKey)) {
        const cachedEntry = featureFlagCache.get(cacheKey);
        // Check if cache is still valid (5 minutes)
        if (Date.now() - cachedEntry.timestamp < 5 * 60 * 1000) {
          console.log('Using cached feature flags for facility:', facilityId);
          return cachedEntry;
        } else {
          // Cache expired, remove it
          featureFlagCache.delete(cacheKey);
        }
      }

      const response = await axios.get(
        `${API_BASE}/internal/facilities/${facilityId}/enabled-features`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const result = response.data;
      
      if (!result || typeof result !== 'object') {
        console.error('Feature flag response is not an object:', result);
        throw new Error('Invalid response format: expected object');
      }
      
      // The endpoint returns { featureIds: [], features: [] }
      if (!result.featureIds || !Array.isArray(result.featureIds)) {
        console.error('Feature flag response missing featureIds property:', result);
        throw new Error('Response missing featureIds property');
      }
      
      // Cache the result for 5 minutes
      const cacheEntry = {
        featureIds: result.featureIds,
        features: result.features || [],
        timestamp: Date.now()
      };
      
      featureFlagCache.set(cacheKey, cacheEntry);
      
      // Clean up old cache entries
      setTimeout(() => {
        if (featureFlagCache.has(cacheKey)) {
          const entry = featureFlagCache.get(cacheKey);
          if (Date.now() - entry.timestamp >= 5 * 60 * 1000) {
            featureFlagCache.delete(cacheKey);
          }
        }
      }, 5 * 60 * 1000); // 5 minutes

      console.log(`Fetched ${result.featureIds.length} enabled features for facility ${facilityId}`);
      return cacheEntry;
      
    } catch (error) {
      console.error('Error fetching enabled features:', error);
      
      // Enhanced error handling with more specific messaging
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          console.warn(`Access denied for facility ${facilityId}. Defaulting to no features enabled.`);
          throw new Error('Access denied to this facility');
        } else if (error.response?.status === 404) {
          console.warn(`Facility ${facilityId} not found. Defaulting to no features enabled.`);
          throw new Error('Facility not found');
        } else if (error.response?.status >= 500) {
          console.warn(`Server error checking features for facility ${facilityId}. Defaulting to no features enabled.`);
          throw new Error('Feature flag system temporarily unavailable');
        } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('network')) {
          console.warn(`Network error checking features for facility ${facilityId}. Defaulting to no features enabled.`);
          throw new Error('Network error - unable to check features');
        }
      }
      
      throw error;
    }
  },

  /**
   * Check if a specific feature is enabled for a facility
   * @param {string} featureKey - Feature key to check
   * @param {string} facilityId - Facility ID
   * @returns {Promise<boolean>} Promise resolving to whether feature is enabled
   */
  isFeatureEnabled: async (featureKey, facilityId) => {
    try {
      const enabledFeatures = await featureFlagService.getEnabledFeatures(facilityId);
      return enabledFeatures.featureIds.includes(featureKey);
    } catch (error) {
      console.error(`Error checking if feature '${featureKey}' is enabled:`, error);
      // Default to disabled for security
      return false;
    }
  },

  /**
   * Check if multiple features are enabled for a facility
   * @param {Array<string>} featureKeys - Array of feature keys to check
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to object with feature keys as properties and boolean values
   */
  areFeaturesEnabled: async (featureKeys, facilityId) => {
    try {
      const enabledFeatures = await featureFlagService.getEnabledFeatures(facilityId);
      const result = {};
      
      featureKeys.forEach(key => {
        result[key] = enabledFeatures.featureIds.includes(key);
      });
      
      return result;
    } catch (error) {
      console.error(`Error checking if features are enabled:`, error);
      // Default all to disabled for security
      const result = {};
      featureKeys.forEach(key => {
        result[key] = false;
      });
      return result;
    }
  },

  /**
   * Get all enabled feature IDs for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array<string>>} Promise resolving to array of enabled feature IDs
   */
  getEnabledFeatureIds: async (facilityId) => {
    try {
      const enabledFeatures = await featureFlagService.getEnabledFeatures(facilityId);
      return enabledFeatures.featureIds;
    } catch (error) {
      console.error(`Error getting enabled feature IDs for facility ${facilityId}:`, error);
      return [];
    }
  },

  /**
   * Get all enabled features with details for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array>} Promise resolving to array of enabled feature objects
   */
  getEnabledFeaturesWithDetails: async (facilityId) => {
    try {
      const enabledFeatures = await featureFlagService.getEnabledFeatures(facilityId);
      return enabledFeatures.features;
    } catch (error) {
      console.error(`Error getting enabled features with details for facility ${facilityId}:`, error);
      return [];
    }
  },

  /**
   * Clear the feature flag cache for a specific facility
   * @param {string} facilityId - Facility ID
   */
  clearCache: (facilityId = null) => {
    if (facilityId) {
      featureFlagCache.delete(facilityId);
      console.log(`Cleared feature flag cache for facility: ${facilityId}`);
    } else {
      featureFlagCache.clear();
      console.log('Cleared all feature flag cache');
    }
  },

  /**
   * Get cache status for debugging
   * @returns {Object} Cache statistics
   */
  getCacheStatus: () => {
    const now = Date.now();
    const entries = Array.from(featureFlagCache.entries());
    const validEntries = entries.filter(([_, entry]) => now - entry.timestamp < 5 * 60 * 1000);
    const expiredEntries = entries.filter(([_, entry]) => now - entry.timestamp >= 5 * 60 * 1000);
    
    return {
      totalEntries: entries.length,
      validEntries: validEntries.length,
      expiredEntries: expiredEntries.length,
      cacheSize: featureFlagCache.size
    };
  },

  /**
   * Force refresh of feature flags for a facility (bypass cache)
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to fresh enabled features object
   */
  forceRefresh: async (facilityId) => {
    // Clear cache for this facility
    featureFlagCache.delete(facilityId);
    console.log(`Forced refresh of feature flags for facility: ${facilityId}`);
    
    // Fetch fresh data
    return await featureFlagService.getEnabledFeatures(facilityId);
  }
};
